import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const MarketingChannelTable = ({ refreshKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editName, setEditName] = useState("");
  const [editError, setEditError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoading(true);
        const res = await axiosConfig({ method: "get", url: "/advertisement/marketing-channel/" });
        const raw = res?.data;
        setRows(raw?.results || (Array.isArray(raw) ? raw : []));
      } catch (e) {
        const errMsg =
          e.response?.data?.name?.[0] ||
          e.response?.data?.detail ||
          e.response?.data?.message ||
          e.message ||
          "Failed to fetch channels";
        toast.error(errMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchChannels();
  }, [refreshKey]);

  const handleEditOpen = (row) => {
    setEditRow(row);
    setEditName(row.name);
    setEditError("");
  };

  const handleEditSave = async () => {
    if (!editName.trim()) return;
    setEditLoading(true);
    setEditError("");
    try {
      const res = await axiosConfig({
        method: "patch",
        url: `/advertisement/marketing-channel/${editRow.id}/`,
        data: { name: editName.trim() },
      });
      setRows((prev) => prev.map((r) => (r.id === editRow.id ? { ...r, name: editName.trim() } : r)));
      toast.success("Channel updated!");
      setEditRow(null);
    } catch (e) {
      const fieldErr = e.response?.data?.name?.[0];
      const errMsg = fieldErr || e.response?.data?.detail || e.response?.data?.message || e.message || "Failed to update channel";
      if (fieldErr) setEditError(fieldErr);
      toast.error(errMsg);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await axiosConfig({ method: "delete", url: `/advertisement/marketing-channel/${deleteRow.id}/`, data: { id: deleteRow.id } });
      setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
      toast.success("Channel deleted!");
      setDeleteRow(null);
    } catch (e) {
      const errMsg =
        e.response?.data?.detail ||
        e.response?.data?.message ||
        e.message ||
        "Failed to delete channel";
      toast.error(errMsg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const btnSx = {
    borderRadius: "8px",
    background: "#016551",
    textTransform: "none",
    fontWeight: 600,
    px: 2.5,
    boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
    "&:hover": { background: "linear-gradient(135deg,#1db954,#17a349)" },
  };

  return (
    <>
      <Paper elevation={0} sx={{ borderRadius: "14px", border: "1px solid #e2e8f0", background: "#fff", boxShadow: "0 1px 6px rgba(0,0,0,0.06)", overflow: "hidden" }}>
        <Box sx={{ px: 2.5, py: 1.8, background: "#04343a", borderRadius: "14px 14px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CampaignIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 16 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff", letterSpacing: 0.5 }}>
              MARKETING CHANNELS
            </Typography>
          </Box>
          {!loading && (
            <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.55)" }}>
              {rows.length} channel{rows.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["S.No", "Channel Name", "Slug", "Status", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={28} sx={{ color: "#04343a" }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>
                    No channels created yet
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, i) => (
                  <TableRow key={row.id ?? i} sx={{ "&:hover": { background: "#f8fafc" } }}>
                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{row.name || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{row.slug || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.is_active === false ? "Inactive" : "Active"}
                        size="small"
                        sx={{
                          fontSize: 10, fontWeight: 700, height: 20,
                          background: row.is_active === false ? "#fee2e2" : "#dcfce7",
                          color: row.is_active === false ? "#dc2626" : "#16a34a",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleEditOpen(row)} sx={{ color: "#016551", "&:hover": { background: "#f0fdf4" } }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteRow(row)} sx={{ color: "#dc2626", "&:hover": { background: "#fef2f2" } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} PaperProps={{ sx: { borderRadius: "14px", minWidth: 380 } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", pb: 1 }}>Edit Channel</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Channel Name"
            value={editName}
            onChange={(e) => { setEditName(e.target.value); setEditError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleEditSave()}
            error={!!editError}
            helperText={editError}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditRow(null)} size="small" sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained" size="small" onClick={handleEditSave}
            disabled={!editName.trim() || editLoading}
            startIcon={editLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={btnSx}
          >
            {editLoading ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)} PaperProps={{ sx: { borderRadius: "14px", minWidth: 340 } }}>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a", pb: 1 }}>Delete Channel</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Are you sure you want to delete <strong>{deleteRow?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteRow(null)} size="small" sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}>Cancel</Button>
          <Button
            variant="contained" size="small" onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{ borderRadius: "8px", background: "#dc2626", textTransform: "none", fontWeight: 600, px: 2.5, "&:hover": { background: "#b91c1c" } }}
          >
            {deleteLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarketingChannelTable;
